import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Autocomplete, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material'

import { isFetchBaseQueryError } from '../../../../../helpers/isFetchBaseQueryError'
import { useMoveInternalUserMutation } from '../../../../api/organizations'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { getValidationErrorMessage } from '../../../../helpers/apiValidationError'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { InternalOrganization, InternalUser } from '../../../organizations/types/Organizations'
import { getMoveTargetLabel, getMoveTargets, getOrganizationLabel } from '../helpers/userListHelpers'

type MoveUserDialogProps = {
  open: boolean
  user?: InternalUser
  organizations: InternalOrganization[]
  onClose: () => void
}

/** Moves a user to another organization; the backend stores the moved user active and without organization admin. */
export const MoveUserDialog: FC<MoveUserDialogProps> = ({ open, user, organizations, onClose }) => {
  const { t } = useTranslation()
  const { notify } = useMutationFeedback()
  const [moveUser, { isLoading }] = useMoveInternalUserMutation()
  const [onlySubscriptions, setOnlySubscriptions] = useState(true)
  const [target, setTarget] = useState<InternalOrganization | null>(null)

  useEffect(() => {
    if (!open) return
    setOnlySubscriptions(true)
    setTarget(null)
  }, [open, user?.id])

  const currentOrganization = organizations.find(({ id }) => id === user?.organization)
  const targets = useMemo(
    () => getMoveTargets(organizations, user?.organization ?? null, onlySubscriptions),
    [organizations, user?.organization, onlySubscriptions]
  )

  const toggleSubscriptions = (checked: boolean) => {
    setOnlySubscriptions(checked)
    if (target && !getMoveTargets(organizations, user?.organization ?? null, checked).some(({ id }) => id === target.id)) setTarget(null)
  }

  const move = async () => {
    if (!user || !target) return
    const organization = getOrganizationLabel(target)
    try {
      const { stored } = await moveUser({ organizationId: target.id, username: user.username, fromOrganizationId: user.organization ?? '' }).unwrap()
      if (stored?.organization !== target.id) {
        notify(t('internal-csm:users_move_not_stored', { username: user.username, organization }), 'error')
        return
      }
      notify(t('internal-csm:users_moved', { username: user.username, organization }), 'success')
      onClose()
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 409) {
        notify(t('internal-csm:users_move_already_member', { username: user.username, organization }), 'error')
        return
      }
      const reason = getValidationErrorMessage(error)
      const message = t('internal-csm:users_move_failed', { username: user.username, organization })
      notify(reason ? `${message}: ${reason}` : message, 'error')
    }
  }

  const name = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : ''
  const sideEffects = user
    ? [!user.active && t('internal-csm:users_move_enables'), user.oadmin && t('internal-csm:users_move_drops_admin')].filter(Boolean)
    : []

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-csm:users_move_title')}
      onClose={onClose}
      onSave={move}
      saveDisabled={!user || !target || isLoading}
      saveLabel={t('internal-csm:users_move_action')}
    >
      {user && (
        <Stack spacing={2} pt={1}>
          <Typography variant="body2">
            {t('internal-csm:users_move_moving')}{' '}
            <Typography component="span" variant="body2" fontWeight="bold">
              {name ? `${name} (${user.username})` : user.username}
            </Typography>
          </Typography>
          <Typography variant="body2">
            {t('internal-csm:users_move_from')}{' '}
            <Typography component="span" variant="body2" fontWeight="bold">
              {currentOrganization ? getOrganizationLabel(currentOrganization) : t('internal-csm:users_move_no_organization')}
            </Typography>
          </Typography>
          <Autocomplete
            size="small"
            options={targets}
            value={target}
            onChange={(_, value) => setTarget(value)}
            getOptionLabel={getMoveTargetLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {getMoveTargetLabel(option)}
              </li>
            )}
            noOptionsText={t('internal-csm:users_move_no_targets')}
            renderInput={(params) => <TextField {...params} label={t('internal-csm:users_move_to')} />}
          />
          <FormControlLabel
            control={<Checkbox checked={onlySubscriptions} onChange={(event) => toggleSubscriptions(event.target.checked)} />}
            label={t('internal-csm:users_move_only_subscriptions')}
          />
          {target && (
            <Typography variant="body2" color="text.secondary">
              {t('internal-csm:users_move_target_id', { id: target.id })}
            </Typography>
          )}
          {sideEffects.length > 0 && (
            <Alert severity="warning">
              {sideEffects.map((text) => (
                <Typography key={String(text)} variant="body2">
                  {text}
                </Typography>
              ))}
            </Alert>
          )}
        </Stack>
      )}
    </InternalFormDialog>
  )
}
